import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './PromoTrialPopup.css';
import appIcon from '../../assets/logo_white_bg.png';
import googlePlayBadge from '../../assets/GET.png';
import appStoreBadge from '../../assets/App_Store_(iOS)-Badge-Logo.wine (1).png';

const APP_FEATURES = [
  'List & manage properties with exact location',
  'Pinpoint coordinates for accurate property mapping',
  'Share property location instantly with buyers',
  'Boost visibility for rent, sale & resale listings',
  'Easy property posting with smart tools',
];

const PromoTrialPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef(null);
  const showDelayRef = useRef(null);
  const SESSION_SHOWN_KEY = '360coordinates_popup_shown';

  const hasBeenShownInSession = () => {
    return sessionStorage.getItem(SESSION_SHOWN_KEY) === 'true';
  };

  const markAsShownInSession = () => {
    sessionStorage.setItem(SESSION_SHOWN_KEY, 'true');
  };

  const shouldShowOnRoute = () => {
    const path = location.pathname.toLowerCase();

    if (path === '/login' || path === '/register') {
      return false;
    }

    // Full download page already shows the same content
    if (path === '/downloadapp') {
      return false;
    }

    if (path.includes('/details/') || path.includes('/viewdetails')) {
      return false;
    }

    // Logged-in dashboard areas (not public marketing pages like /buyer or /seller)
    const dashboardExactPaths = new Set([
      '/buyer-dashboard',
      '/buyerhome',
      '/buy',
      '/rent',
      '/pghostel',
      '/projects',
      '/buyerprofile',
      '/chatus',
      '/chat',
      '/searchresults',
      '/seller-dashboard',
    ]);
    if (dashboardExactPaths.has(path)) {
      return false;
    }

    const dashboardPrefixes = [
      '/seller/properties',
      '/seller/inquiries',
      '/agent/',
      '/upcoming-project/',
    ];
    if (dashboardPrefixes.some((prefix) => path.startsWith(prefix))) {
      return false;
    }

    return true;
  };

  // Manual close handler - just closes without disabling
  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 250); // Match CSS animation duration
    
    // Clear timeout if still running
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const PLAY_STORE_URL =
    'https://play.google.com/store/apps/details?id=com.indiapropertys.app360';
  const APP_STORE_URL =
    'https://apps.apple.com/in/app/360coordinates/id6768521931';

  const handlePlayStoreClick = () => {
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const handleAppStoreClick = () => {
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  useEffect(() => {
    // Legacy: store buttons used to disable the popup forever — clear so it can show again
    localStorage.removeItem('360coordinates_popup_disabled');

    // Don't show if popup has already been shown in this session
    if (hasBeenShownInSession()) {
      return;
    }

    // Check if should show on current route
    if (!shouldShowOnRoute()) {
      return;
    }

    // Clear any existing timeouts
    if (showDelayRef.current) {
      clearTimeout(showDelayRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Small delay to ensure smooth animation on page load
    showDelayRef.current = setTimeout(() => {
      setIsAnimating(true);
      setIsVisible(true);
      
      // Mark as shown in this session so it won't appear again
      markAsShownInSession();
      
      // Popup will remain visible until user clicks download or close button
    }, 300); // Small delay for smooth page load

    // Cleanup on unmount or route change
    return () => {
      if (showDelayRef.current) {
        clearTimeout(showDelayRef.current);
        showDelayRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [location.pathname]); // Re-check when route changes

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`promo-trial-popup-overlay ${isAnimating ? 'promo-trial-popup-visible' : 'promo-trial-popup-hidden'}`}>
      <div className="promo-trial-popup-container">
        <button
          type="button"
          className="promo-trial-popup-close"
          onClick={handleClose}
          aria-label="Close popup"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <aside className="promo-trial-popup-brand">
          <img className="promo-trial-popup-app-icon" src={appIcon} alt="360Coordinates App" />
          <span className="promo-trial-popup-brand-name">360Coordinates</span>
        </aside>

        <div className="promo-trial-popup-main">
          <h2 className="promo-trial-popup-title">Download the app</h2>
          <p className="promo-trial-popup-subtitle">List, search and share properties with exact map locations</p>

          <ul className="promo-trial-popup-features">
            {APP_FEATURES.map((feature) => (
              <li key={feature} className="promo-trial-popup-feature">
                <span className="promo-trial-popup-feature-icon" aria-hidden>✓</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <div className="promo-trial-popup-store-buttons">
            <button
              type="button"
              className="promo-trial-popup-store-badge-btn"
              onClick={handlePlayStoreClick}
              aria-label="Get it on Google Play"
            >
              <span className="promo-trial-popup-store-badge-wrap">
                <img
                  src={googlePlayBadge}
                  alt="Get it on Google Play"
                  className="promo-trial-popup-store-badge"
                />
              </span>
            </button>

            <button
              type="button"
              className="promo-trial-popup-store-badge-btn promo-trial-popup-store-badge-btn--apple"
              onClick={handleAppStoreClick}
              aria-label="Download on the App Store"
            >
              <span className="promo-trial-popup-store-badge-wrap promo-trial-popup-store-badge-wrap--apple">
                <img
                  src={appStoreBadge}
                  alt="Download on the App Store"
                  className="promo-trial-popup-store-badge promo-trial-popup-store-badge--apple"
                />
              </span>
            </button>
          </div>

          <p className="promo-trial-popup-limited">No signup required to download</p>
        </div>
      </div>
    </div>
  );
};

export default PromoTrialPopup;